import { useLocation, Route } from 'react-router-dom'
import { FaroRoutes } from '@grafana/faro-react'
import { Suspense, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { StaticRoutes } from './StaticRoutes'
import { RegularPlanRoutes } from './RegularPlanRoutes'
import { MutualFundRoutes } from './MutualFundRoutes'
import { PaymentRoutes } from './PaymentRoutes'
import { AllocationRoutes } from '../features/allocation/AllocationRoutes'
import { RetryRoute } from './RetryRoute'
import MetaDataProvider from '../state-management/providers/MetaDataProvider'
import MFExecutionProvider from '../state-management/providers/MFExecutionProvider'
import OneTimeProvider from '../state-management/providers/BuyProvider'
import SIPProvider from '../state-management/providers/SIPProvider'
import WithdrawProvider from '../state-management/providers/WithdrawProvider'
import SWPProvider from '../state-management/providers/SWPProvider'
import TransferProvider from '../state-management/providers/TransferProvider'
import { PaymentPageType } from '../types/mf-execution.types'
import { extractRouteParams, getRouteType } from './utils'
import { useUserDetails } from '../hooks/useUserDetails'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { FallBackPage } from '../FallBackPage'
import { markWebviewBoot } from '../utils/utils'
import { shouldBlockHardwareBack } from '../utils/hardwareBackConfig'
import { disableHardwareBack } from '../utils/appBridge'
import { DebugToolRoot } from '../components/debug-tool/DebugToolRoot'

const ErrorBoundaryWithSentry = ({ children }: { children: React.ReactNode }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={<FallBackPage />}
      beforeCapture={(scope, error) => {
        markWebviewBoot('failed', { message: 'webview_failed_in_route', error })
        scope.setTag('errorBoundary', 'true')
        console.error('Error Boundary triggered:', error)
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

export const AllRoutes = () => {
  const { userDetails, isLoading: isUserDetailsLoading } = useUserDetails()

  const location = useLocation()

  useEffect(() => {
    const pathname = location?.pathname ?? window?.location?.pathname ?? ''
    disableHardwareBack(shouldBlockHardwareBack(pathname))
  }, [location.pathname])

  const { isin, from, paymentType } = extractRouteParams(location.pathname, location.search)
  const { isMutualFundRoute, isRegularPlanRoute, isAllocationRoute } = getRouteType(location.pathname)

  const getPaymentProvider = () => {
    switch (paymentType) {
      case PaymentPageType.BUY:
        return OneTimeProvider
      case PaymentPageType.SIP:
        return SIPProvider
      case PaymentPageType.WITHDRAW:
        return WithdrawProvider
      case PaymentPageType.SWP:
        return SWPProvider
      case PaymentPageType.TRANSFER:
        return TransferProvider
      default:
        return null
    }
  }

  const routesContent = () => {
    if (isMutualFundRoute) {
      const PaymentProvider = getPaymentProvider()

      return (
        <ErrorBoundaryWithSentry>
          <MetaDataProvider isin={isin} from={from}>
            <MFExecutionProvider>
              {PaymentProvider ? (
                <PaymentProvider>
                  <Suspense fallback={<LoadingSpinner />}>
                    <FaroRoutes>
                      {...MutualFundRoutes()}
                      {...PaymentRoutes()}
                    </FaroRoutes>
                  </Suspense>
                </PaymentProvider>
              ) : (
                <Suspense fallback={<LoadingSpinner />}>
                  <FaroRoutes location={location}>
                    {...MutualFundRoutes()}
                    {...PaymentRoutes()}
                  </FaroRoutes>
                </Suspense>
              )}
            </MFExecutionProvider>
          </MetaDataProvider>
        </ErrorBoundaryWithSentry>
      )
    }

    if (isRegularPlanRoute) {
      return (
        <ErrorBoundaryWithSentry>
          <MetaDataProvider isin={isin} from={from} disableOnReady={true}>
            <Suspense fallback={<LoadingSpinner />}>
              <FaroRoutes>{...RegularPlanRoutes()}</FaroRoutes>
            </Suspense>
          </MetaDataProvider>
        </ErrorBoundaryWithSentry>
      )
    }

    if (isAllocationRoute) {
      return (
        <ErrorBoundaryWithSentry>
          <Suspense fallback={<LoadingSpinner />}>
            <AllocationRoutes />
          </Suspense>
        </ErrorBoundaryWithSentry>
      )
    }

    return (
      <ErrorBoundaryWithSentry>
        <Suspense fallback={<LoadingSpinner />}>
          <FaroRoutes location={location}>
            {...StaticRoutes()}
            <Route path='*' element={<RetryRoute />} />
          </FaroRoutes>
        </Suspense>
      </ErrorBoundaryWithSentry>
    )
  }

  return (
    <DebugToolRoot
      phoneNumber={userDetails?.mobileNumber ?? ''}
      isUserDetailsLoading={isUserDetailsLoading}
    >
      {routesContent()}
    </DebugToolRoot>
  )
}
